import React, { useState, useEffect, useMemo } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useLanguage } from '../../contexts/LanguageContext';
import {
  getFinancedDeal,
  updateFinancedDeal,
} from '../../services/financedDealsService';
import { updatePrivateLenderOffer } from '../../services/privateLenderOfferService';
import {
  getFinancedDealTranchesByDeal,
  createFinancedDealTranche,
  deleteFinancedDealTranche,
} from '../../services/financedDealTranchesService';
import {
  getInterestPaymentsForDeal,
  createOrUpdateInterestPayment,
  computeExpectedInterestForPeriod,
  getExpectedPaymentDateForPeriod,
  getSchedulePeriodsForDeal,
  normalizeInterestPeriodAnchorDay,
} from '../../services/interestPaymentsService';
import { subscribeLenderStagedProperties } from '../../services/lenderStagedPropertiesService';
import { subscribePrivateLenderOffersByLender } from '../../services/privateLenderOfferService';
import {
  getLenderSettings,
  setLenderSettings,
  subscribeLenderSettings,
  PAYMENT_EMAIL_DOMAIN,
  PAYMENT_EMAIL_PREFIX_TAKEN,
} from '../../services/lenderSettingsService';
import { X, Plus, DollarSign, Calendar, AlertCircle, FileText, Settings, ChevronDown, ChevronRight } from 'lucide-react';
import { serverTimestamp } from 'firebase/firestore';
import InterestReceivedReport from './InterestReceivedReport';
import { getInterestPaymentEventsForPeriod, createRefundIssuedEvent } from '../../services/interestPaymentEventsService';
import { formatStagingPrincipal, roundMoneyToCents } from '../../utils/stagingAmountFormat';
import EscapeCloseLayer from '../shared/EscapeCloseLayer';

function formatAmount(n) {
  if (n == null || typeof n !== 'number' || Number.isNaN(n)) return '—';
  const s = String(Math.max(0, Math.floor(n)));
  const withSpaces = s.replace(/\B(?=(\d{3})+(?!\d))/g, ' ');
  return `${withSpaces} $`;
}

/** Format decimal amount for display (e.g. 1234.5 → "1 234.50 $") */
function formatPaidAmountDisplay(value) {
  if (value == null || value === '') return '';
  const num = typeof value === 'number' ? value : Number(String(value).replace(/\s/g, '').replace(',', '.').replace(/\$/g, ''));
  if (Number.isNaN(num) || num < 0) return '';
  const fixed = num.toFixed(2);
  const [intPart, decPart] = fixed.split('.');
  const withSpaces = intPart.replace(/\B(?=(\d{3})+(?!\d))/g, ' ');
  return `${withSpaces}.${decPart} $`;
}

/** Parse displayed paid amount back to number */
function parsePaidAmountDisplay(str) {
  if (!str || typeof str !== 'string') return NaN;
  const cleaned = str.replace(/\s/g, '').replace(/\$/g, '').replace(',', '.').trim();
  return cleaned === '' ? NaN : Number(cleaned);
}

function formatDateWithLocale(str, locale) {
  if (!str || typeof str !== 'string') return '—';
  const d = str.slice(0, 10);
  if (!d) return '—';
  try {
    const dateLocale = locale === 'fr' ? 'fr-CA' : 'en-CA';
    return new Date(d + 'T12:00:00').toLocaleDateString(dateLocale, { day: 'numeric', month: 'short', year: 'numeric' });
  } catch {
    return d;
  }
}

function daysUntil(dateStr) {
  if (!dateStr || typeof dateStr !== 'string') return null;
  const d = new Date(dateStr.slice(0, 10) + 'T12:00:00');
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  d.setHours(0, 0, 0, 0);
  return Math.ceil((d - today) / (1000 * 60 * 60 * 24));
}

const PrivateLenderLoansPage = ({ financedDeals = [], properties = [], clients = [], onOpenProperty, initialDetailDeal = null, onConsumedInitialDetailDeal = null }) => {
  const { currentUser, refreshFinancedDeals } = useAuth();
  const { t, language } = useLanguage();
  const formatDate = (str) => formatDateWithLocale(str, language);
  const [detailDeal, setDetailDeal] = useState(null);
  const [detailTranches, setDetailTranches] = useState([]);
  const [addTrancheDealId, setAddTrancheDealId] = useState(null);
  const [markingDisbursed, setMarkingDisbursed] = useState(null);
  const [cancellingDealId, setCancellingDealId] = useState(null);
  const [addingTranche, setAddingTranche] = useState(false);
  const [deletingTrancheId, setDeletingTrancheId] = useState(null);
  const [trancheForm, setTrancheForm] = useState({ amount: '', startDate: '', interestRate: '', note: '' });
  const [trancheError, setTrancheError] = useState('');
  const [detailInterestPayments, setDetailInterestPayments] = useState([]);
  const [markingPayment, setMarkingPayment] = useState(false);
  const [showMarkPaymentForm, setShowMarkPaymentForm] = useState(false);
  const [markPaymentPeriodKey, setMarkPaymentPeriodKey] = useState(null);
  const [markPaymentForm, setMarkPaymentForm] = useState({ paidAmount: '', paidAt: '', discrepancyNote: '' });
  const [periodDetailModal, setPeriodDetailModal] = useState(null); // { periodKey, periodStart, monthLabel, expectedAmount, paidAmount, creditApplied, status }
  const [periodEvents, setPeriodEvents] = useState([]);
  const [periodEventsLoading, setPeriodEventsLoading] = useState(false);
  const [refundForm, setRefundForm] = useState({ occurredAt: new Date().toISOString().slice(0, 10), note: '' });
  const [submittingRefund, setSubmittingRefund] = useState(false);
  const [showReport, setShowReport] = useState(false);
  const [lenderSettingsExpanded, setLenderSettingsExpanded] = useState(false);
  const [lenderSettings, setLenderSettingsState] = useState(null);
  const [lenderSettingsForm, setLenderSettingsForm] = useState({
    fundCode: '',
    interestPaymentEmailLocalPart: '',
    autoMarkPaymentsEnabled: false,
    fundManagerInterestQuotaPct: '20',
    interestPeriodAnchorDay: '1',
    fundInConfiguration: false,
  });
  const [stagedPropertiesList, setStagedPropertiesList] = useState([]);
  const [savingLenderSettings, setSavingLenderSettings] = useState(false);
  const [lenderSettingsSaveError, setLenderSettingsSaveError] = useState(null);
  const [lenderSettingsSaved, setLenderSettingsSaved] = useState(false);
  const [showAutoMarkInfoModal, setShowAutoMarkInfoModal] = useState(false);

  const AUTO_MARK_INFO_SEEN_KEY = 'rentalyzer_lender_autoMarkInfoSeen';

  useEffect(() => {
    if (!currentUser?.uid) return;
    return subscribePrivateLenderOffersByLender(currentUser.uid, () => {});
  }, [currentUser?.uid]);

  useEffect(() => {
    if (!currentUser?.uid) return;
    return subscribeLenderSettings(currentUser.uid, (data) => {
      setLenderSettingsState(data);
      setLenderSettingsForm({
        fundCode: data?.fundCode || '',
        interestPaymentEmailLocalPart: data?.interestPaymentEmailLocalPart || '',
        autoMarkPaymentsEnabled: data?.autoMarkPaymentsEnabled ?? false,
        fundManagerInterestQuotaPct: String((data && data.fundManagerInterestQuotaPct) ?? 20),
        interestPeriodAnchorDay: String(
          normalizeInterestPeriodAnchorDay(data?.interestPeriodAnchorDay)
        ),
        fundInConfiguration: data?.fundInConfiguration === true,
      });
    });
  }, [currentUser?.uid]);

  useEffect(() => {
    if (!currentUser?.uid) return;
    return subscribeLenderStagedProperties(currentUser.uid, setStagedPropertiesList);
  }, [currentUser?.uid]);

  const handleSaveLenderSettings = async (e) => {
    e.preventDefault();
    if (!currentUser?.uid) return;
    setLenderSettingsSaveError(null);
    setLenderSettingsSaved(false);
    setSavingLenderSettings(true);
    try {
      const q = parseFloat(String(lenderSettingsForm.fundManagerInterestQuotaPct).replace(',', '.'), 10);
      const anchor = normalizeInterestPeriodAnchorDay(lenderSettingsForm.interestPeriodAnchorDay);
      await setLenderSettings(currentUser.uid, {
        fundCode: lenderSettingsForm.fundCode.trim() || null,
        interestPaymentEmailLocalPart: lenderSettingsForm.interestPaymentEmailLocalPart.trim() || null,
        autoMarkPaymentsEnabled: lenderSettingsForm.autoMarkPaymentsEnabled,
        fundManagerInterestQuotaPct: Number.isFinite(q) ? Math.min(100, Math.max(0, q)) : 20,
        interestPeriodAnchorDay: anchor,
        fundInConfiguration: lenderSettingsForm.fundInConfiguration === true,
      });
      setLenderSettingsSaved(true);
      setTimeout(() => setLenderSettingsSaved(false), 3000);
    } catch (err) {
      const msg = err?.message || String(err);
      const prefixTaken =
        msg === PAYMENT_EMAIL_PREFIX_TAKEN ||
        msg.includes(PAYMENT_EMAIL_PREFIX_TAKEN) ||
        err?.code === 'functions/already-exists';
      setLenderSettingsSaveError(prefixTaken ? t('lenderSettings.paymentEmailPrefixTaken') : msg);
    } finally {
      setSavingLenderSettings(false);
    }
  };

  useEffect(() => {
    if (initialDetailDeal?.id && onConsumedInitialDetailDeal) {
      setDetailDeal(initialDetailDeal);
      onConsumedInitialDetailDeal();
    }
  }, [initialDetailDeal?.id, onConsumedInitialDetailDeal]);

  useEffect(() => {
    if (!detailDeal?.id) {
      setDetailTranches([]);
      return;
    }
    let cancelled = false;
    getFinancedDealTranchesByDeal(detailDeal.id).then((list) => {
      if (!cancelled) setDetailTranches(list);
    });
    return () => { cancelled = true; };
  }, [detailDeal?.id]);

  useEffect(() => {
    if (!detailDeal?.id || !detailDeal.fundsDisbursedAt) {
      setDetailInterestPayments([]);
      return;
    }
    let cancelled = false;
    getInterestPaymentsForDeal(detailDeal.id).then((list) => {
      if (!cancelled) setDetailInterestPayments(list);
    });
    return () => { cancelled = true; };
  }, [detailDeal?.id, detailDeal?.fundsDisbursedAt]);

  const interestPeriodAnchor = normalizeInterestPeriodAnchorDay(lenderSettings?.interestPeriodAnchorDay);

  const paymentSchedule = useMemo(() => {
    if (!detailDeal?.fundsDisbursedAt || !detailDeal?.startDate || !detailDeal?.maturityDate) return [];
    const periods = getSchedulePeriodsForDeal(detailDeal, { interestPeriodAnchorDay: interestPeriodAnchor });
    return periods.map(({ periodKey, periodStart, periodEnd }) => {
      const expectedAmount = computeExpectedInterestForPeriod(detailDeal, detailTranches, periodStart, periodEnd);
      const expectedByDate = getExpectedPaymentDateForPeriod(detailDeal, periodKey, interestPeriodAnchor);
      const payment = detailInterestPayments.find((p) => p.periodKey === periodKey || p.periodStart === periodStart);
      const status = payment?.status || (expectedAmount > 0 ? 'pending' : 'n/a');
      const paidAmount = payment?.paidAmount ?? null;
      const creditApplied = payment?.creditApplied ?? 0;
      const [y, m] = periodKey.split('-').map(Number);
      const monthLabel =
        interestPeriodAnchor === 1
          ? new Date(y, m - 1, 1).toLocaleDateString(language === 'fr' ? 'fr-CA' : 'en-CA', {
              month: 'short',
              year: 'numeric',
            })
          : `${formatDateWithLocale(periodStart, language)} – ${formatDateWithLocale(periodEnd, language)}`;
      return { periodKey, periodStart, periodEnd, expectedAmount, expectedByDate, status, paidAmount, creditApplied, monthLabel };
    });
  }, [detailDeal, detailTranches, detailInterestPayments, language, interestPeriodAnchor]);

  const openPeriodDetail = async (row) => {
    if (!detailDeal?.id || !row?.periodStart) return;
    setPeriodDetailModal(row);
    setPeriodEvents([]);
    setPeriodEventsLoading(true);
    try {
      const events = await getInterestPaymentEventsForPeriod(detailDeal.id, row.periodStart);
      setPeriodEvents(events);
    } finally {
      setPeriodEventsLoading(false);
    }
  };

  const openMarkPaymentForm = (periodKey, expectedAmount) => {
    setMarkPaymentPeriodKey(periodKey);
    setMarkPaymentForm({
      paidAmount: expectedAmount > 0 ? formatPaidAmountDisplay(expectedAmount) : '',
      paidAt: new Date().toISOString().slice(0, 10),
      discrepancyNote: '',
    });
    setShowMarkPaymentForm(true);
  };

  const handleSavePayment = async (e) => {
    e.preventDefault();
    if (!detailDeal?.id || !currentUser?.uid || !markPaymentPeriodKey) return;
    const paid = parsePaidAmountDisplay(markPaymentForm.paidAmount);
    if (Number.isNaN(paid) || paid < 0) return;
    const row = paymentSchedule.find((r) => r.periodKey === markPaymentPeriodKey);
    const expectedAmount = row?.expectedAmount ?? 0;
    setMarkingPayment(true);
    try {
      const paidAt = markPaymentForm.paidAt
        ? new Date(markPaymentForm.paidAt + 'T12:00:00')
        : new Date();
      await createOrUpdateInterestPayment(
        detailDeal.id,
        currentUser.uid,
        markPaymentPeriodKey,
        { paidAmount: paid, paidAt, discrepancyNote: markPaymentForm.discrepancyNote },
        expectedAmount,
        interestPeriodAnchor
      );
      const list = await getInterestPaymentsForDeal(detailDeal.id);
      setDetailInterestPayments(list);
      setShowMarkPaymentForm(false);
      setMarkPaymentPeriodKey(null);
    } catch (err) {
      console.error(err);
    } finally {
      setMarkingPayment(false);
    }
  };

  const getPropertyForDeal = (deal) => (deal?.propertyId ? properties.find((p) => p.id === deal.propertyId) : null);
  const getClientLabel = (clientId) => {
    if (!clientId || clientId === '_sans_client_') return null;
    const c = clients.find((x) => x.id === clientId);
    return c ? (c.name || c.email || c.id) : null;
  };

  const stagedDraftPrincipalTotal = useMemo(() => {
    let s = 0;
    for (const row of stagedPropertiesList) {
      for (const tr of row.loanTranches || []) {
        const a = roundMoneyToCents(Number(tr.amount));
        s += Number.isFinite(a) ? a : 0;
      }
    }
    return roundMoneyToCents(s);
  }, [stagedPropertiesList]);

  const activeDeals = (financedDeals || []).filter((d) => d.status !== 'cancelled');
  const disbursedDeals = activeDeals.filter((d) => d.fundsDisbursedAt);
  const dealsAwaitingDisbursement = activeDeals.filter((d) => d.status === 'active' && !d.fundsDisbursedAt);
  const dealsMaturitySoon = disbursedDeals.filter((d) => {
    const days = daysUntil(d.maturityDate);
    return days != null && days >= 0 && days <= 90;
  });
  const totalCapital = disbursedDeals.reduce((sum, d) => sum + (Number(d.principalAmount) || 0), 0);

  const openDetail = async (deal) => {
    const full = await getFinancedDeal(deal.id);
    setDetailDeal(full || deal);
  };

  const handleMarkDisbursed = async (dealId) => {
    setMarkingDisbursed(dealId);
    try {
      await updateFinancedDeal(dealId, { fundsDisbursedAt: serverTimestamp() });
      if (typeof refreshFinancedDeals === 'function') await refreshFinancedDeals();
      setDetailDeal((d) => (d?.id === dealId ? null : d));
    } finally {
      setMarkingDisbursed(null);
    }
  };

  const handleCancelDeal = async (deal) => {
    if (!window.confirm(t('privateLenderLoans.cancelDealConfirm'))) return;
    setCancellingDealId(deal.id);
    try {
      await updateFinancedDeal(deal.id, { status: 'cancelled', cancelledAt: serverTimestamp() });
      if (deal.offerId) await updatePrivateLenderOffer(deal.offerId, { status: 'deal_cancelled' });
      if (typeof refreshFinancedDeals === 'function') await refreshFinancedDeals();
      setDetailDeal((d) => (d?.id === deal.id ? null : d));
    } finally {
      setCancellingDealId(null);
    }
  };

  const openAddTranche = (dealId) => {
    setAddTrancheDealId(dealId);
    setTrancheForm({ amount: '', startDate: new Date().toISOString().slice(0, 10), interestRate: '', note: '' });
    setTrancheError('');
  };

  const handleAddTranche = async (e) => {
    e.preventDefault();
    if (!addTrancheDealId) return;
    const amount = parsePaidAmountDisplay(trancheForm.amount);
    const startDate = trancheForm.startDate?.trim();
    if (!startDate || Number.isNaN(amount) || amount <= 0) {
      setTrancheError(t('privateLenderLoans.trancheAmountDateRequired') || 'Amount and start date required.');
      return;
    }
    setAddingTranche(true);
    setTrancheError('');
    try {
      const interestRate = trancheForm.interestRate.trim() ? Number(trancheForm.interestRate.replace(',', '.')) : null;
      await createFinancedDealTranche({
        dealId: addTrancheDealId,
        amount,
        startDate,
        interestRate: Number.isNaN(interestRate) ? null : interestRate,
        type: 'additionnel',
        note: trancheForm.note?.trim() || undefined,
      });
      if (detailDeal?.id === addTrancheDealId) {
        const list = await getFinancedDealTranchesByDeal(addTrancheDealId);
        setDetailTranches(list);
      }
      setAddTrancheDealId(null);
    } catch (err) {
      setTrancheError(err?.message || 'Error');
    } finally {
      setAddingTranche(false);
    }
  };

  const handleDeleteTranche = async (tranche) => {
    const todayStr = new Date().toISOString().slice(0, 10);
    const startStr = (tranche.startDate || '').slice(0, 10);
    if (!startStr || startStr <= todayStr) return; // only allow if effective date not yet reached
    if (!window.confirm(t('privateLenderLoans.deleteTrancheConfirm'))) return;
    setDeletingTrancheId(tranche.id);
    setTrancheError('');
    try {
      await deleteFinancedDealTranche(tranche.id);
      if (detailDeal?.id) {
        const list = await getFinancedDealTranchesByDeal(detailDeal.id);
        setDetailTranches(list);
      }
    } catch (err) {
      setTrancheError(err?.message || 'Error');
    } finally {
      setDeletingTrancheId(null);
    }
  };

  if (showReport) {
    return (
      <div className="space-y-6">
        <InterestReceivedReport
          financedDeals={financedDeals}
          properties={properties}
          onBack={() => setShowReport(false)}
          interestPeriodAnchorDay={interestPeriodAnchor}
        />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h2 className="text-xl font-semibold text-gray-800 flex items-center gap-2">
          <DollarSign className="w-5 h-5 text-indigo-600" />
          {t('privateLenderLoans.title')}
        </h2>
        <button
          type="button"
          onClick={() => setShowReport(true)}
          className="inline-flex items-center gap-2 px-3 py-1.5 text-sm font-medium text-indigo-600 hover:text-indigo-800 hover:bg-indigo-50 rounded-lg border border-indigo-200"
        >
          <FileText className="w-4 h-4" />
          {t('interestReport.showReport')}
        </button>
      </div>

      {/* Paramètres de paiement (prêteur) */}
      <div className="border border-gray-200 rounded-lg overflow-hidden bg-white">
        <button
          type="button"
          onClick={() => setLenderSettingsExpanded((x) => !x)}
          className="w-full flex items-center gap-2 px-4 py-3 text-left font-medium text-gray-800 hover:bg-gray-50"
        >
          {lenderSettingsExpanded ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
          <Settings className="w-4 h-4 text-gray-500" />
          {t('lenderSettings.title')}
        </button>
        {lenderSettingsExpanded && (
          <div className="px-4 pb-4 pt-0 border-t border-gray-100">
            <form onSubmit={handleSaveLenderSettings} className="space-y-4 max-w-xl">
              <p className="text-sm text-gray-600">{t('lenderSettings.help')}</p>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">{t('lenderSettings.fundCode')}</label>
                <input
                  type="text"
                  value={lenderSettingsForm.fundCode}
                  onChange={(e) => setLenderSettingsForm((f) => ({ ...f, fundCode: e.target.value }))}
                  placeholder="FND1"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm uppercase"
                  maxLength={20}
                />
                <p className="text-xs text-gray-500 mt-0.5">{t('lenderSettings.fundCodeHelp')}</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">{t('lenderSettings.fundManagerQuotaPct')}</label>
                <input
                  type="number"
                  min={0}
                  max={100}
                  step={0.5}
                  value={lenderSettingsForm.fundManagerInterestQuotaPct}
                  onChange={(e) => setLenderSettingsForm((f) => ({ ...f, fundManagerInterestQuotaPct: e.target.value }))}
                  className="w-40 px-3 py-2 border border-gray-300 rounded-lg text-sm"
                />
                <p className="text-xs text-gray-500 mt-0.5">{t('lenderSettings.fundManagerQuotaPctHelp')}</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">{t('lenderSettings.paymentEmailPrefix')}</label>
                <div className="flex items-center gap-2">
                  <input
                    type="text"
                    value={lenderSettingsForm.interestPaymentEmailLocalPart}
                    onChange={(e) => setLenderSettingsForm((f) => ({ ...f, interestPaymentEmailLocalPart: e.target.value.toLowerCase().replace(/[^a-z0-9.-]/g, '') }))}
                    placeholder="paiements"
                    className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm"
                    maxLength={64}
                  />
                  <span className="text-gray-500 text-sm">@{PAYMENT_EMAIL_DOMAIN}</span>
                </div>
                <p className="text-xs text-gray-500 mt-0.5">{t('lenderSettings.paymentEmailHelp').replace('rentalyzer.ca', PAYMENT_EMAIL_DOMAIN)}</p>
              </div>
              <div className="flex flex-wrap items-center gap-2">
                <input
                  type="checkbox"
                  id="autoMarkPayments"
                  checked={lenderSettingsForm.autoMarkPaymentsEnabled}
                  onChange={(e) => {
                    const checked = e.target.checked;
                    setLenderSettingsForm((f) => ({ ...f, autoMarkPaymentsEnabled: checked }));
                    if (checked && !localStorage.getItem(AUTO_MARK_INFO_SEEN_KEY)) {
                      setShowAutoMarkInfoModal(true);
                      localStorage.setItem(AUTO_MARK_INFO_SEEN_KEY, '1');
                    }
                  }}
                  className="rounded border-gray-300"
                />
                <label htmlFor="autoMarkPayments" className="text-sm text-gray-700">{t('lenderSettings.autoMarkPayments')}</label>
                <button
                  type="button"
                  onClick={() => setShowAutoMarkInfoModal(true)}
                  className="text-sm text-indigo-600 hover:underline"
                >
                  {t('lenderSettings.moreInfo')}
                </button>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1" htmlFor="interestPeriodAnchorDay">
                  {t('lenderSettings.interestPeriodAnchorDay')}
                </label>
                <input
                  id="interestPeriodAnchorDay"
                  type="number"
                  min={1}
                  max={28}
                  value={lenderSettingsForm.interestPeriodAnchorDay}
                  onChange={(e) =>
                    setLenderSettingsForm((f) => ({ ...f, interestPeriodAnchorDay: e.target.value }))
                  }
                  className="w-28 px-3 py-2 border border-gray-300 rounded-lg text-sm"
                />
                <p className="text-xs text-gray-500 mt-0.5">{t('lenderSettings.interestPeriodAnchorDayHelp')}</p>
              </div>
              <div className="flex gap-3 items-start">
                <input
                  type="checkbox"
                  id="fundInConfiguration"
                  checked={lenderSettingsForm.fundInConfiguration}
                  onChange={(e) =>
                    setLenderSettingsForm((f) => ({ ...f, fundInConfiguration: e.target.checked }))
                  }
                  className="rounded border-gray-300 shrink-0 mt-1.5"
                />
                <div className="min-w-0 flex-1">
                  <label htmlFor="fundInConfiguration" className="text-sm text-gray-700 cursor-pointer">
                    {t('lenderSettings.fundInConfiguration')}
                  </label>
                  <p className="text-xs text-gray-500 mt-0.5">{t('lenderSettings.fundInConfigurationHelp')}</p>
                </div>
              </div>
              {lenderSettingsSaveError && (
              <p className="text-sm text-red-600 bg-red-50 px-3 py-2 rounded-lg">{lenderSettingsSaveError}</p>
            )}
              {lenderSettingsSaved && (
                <p className="text-sm text-green-700 bg-green-50 px-3 py-2 rounded-lg">{t('lenderSettings.saved')}</p>
              )}
              <button
                type="submit"
                disabled={savingLenderSettings}
                className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50 text-sm font-medium"
              >
                {savingLenderSettings ? t('lenderSettings.saving') : t('lenderSettings.save')}
              </button>
            </form>
          </div>
        )}
      </div>

      {/* Modale Plus d'information – marquage automatique Interac */}
      {showAutoMarkInfoModal && (
        <EscapeCloseLayer onClose={() => setShowAutoMarkInfoModal(false)}>
        <div className="fixed inset-0 z-[220] rentalyzer-modal-overlay flex items-center justify-center p-4 bg-black/50">
          <div className="bg-white rounded-lg shadow-xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between p-4 border-b">
              <h3 className="text-lg font-semibold text-gray-900">{t('lenderSettings.autoMarkInfoModalTitle')}</h3>
              <button type="button" onClick={() => setShowAutoMarkInfoModal(false)} className="p-1 rounded hover:bg-gray-100">
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="p-4 space-y-3 text-sm text-gray-700">
              <p>{t('lenderSettings.autoMarkInfoModalIntro').replace(/rentalyzer\.ca/g, PAYMENT_EMAIL_DOMAIN)}</p>
              <ul className="list-disc list-inside space-y-2 pl-1">
                <li>{t('lenderSettings.autoMarkInfoModalStep1')}</li>
                <li>{t('lenderSettings.autoMarkInfoModalStep2')}</li>
                <li>{t('lenderSettings.autoMarkInfoModalStep3')}</li>
              </ul>
            </div>
            <div className="p-4 border-t flex justify-end">
              <button type="button" onClick={() => setShowAutoMarkInfoModal(false)} className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 text-sm font-medium">
                {t('close')}
              </button>
            </div>
          </div>
        </div>
        </EscapeCloseLayer>
      )}

      {stagedPropertiesList.length > 0 && (
        <div className="rounded-lg border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-800">
          <p className="font-medium text-slate-900">{t('privateLenderLoans.stagingDraftSummaryTitle')}</p>
          <p className="text-slate-600 mt-1">
            {t('privateLenderLoans.stagingDraftSummaryBody')
              .replace('{buildings}', String(stagedPropertiesList.length))
              .replace('{principal}', formatStagingPrincipal(stagedDraftPrincipalTotal, language))}
          </p>
        </div>
      )}

      {/* Indicateurs */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <div className="bg-white rounded-lg border border-gray-200 p-4">
          <p className="text-sm text-gray-500">{t('privateLenderLoans.activeLoans')}</p>
          <p className="text-2xl font-semibold text-gray-800">{disbursedDeals.length}</p>
        </div>
        <div className="bg-white rounded-lg border border-gray-200 p-4">
          <p className="text-sm text-gray-500">{t('privateLenderLoans.totalCapital')}</p>
          <p className="text-2xl font-semibold text-gray-800">{formatAmount(totalCapital)}</p>
        </div>
        <div className="bg-white rounded-lg border border-gray-200 p-4">
          <p className="text-sm text-gray-500">{t('privateLenderLoans.awaitingDisbursement')}</p>
          <p className="text-2xl font-semibold text-amber-600">{dealsAwaitingDisbursement.length}</p>
        </div>
      </div>

      {/* En attente de décaissement */}
      {dealsAwaitingDisbursement.length > 0 && (
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
          <h3 className="font-medium text-amber-900 flex items-center gap-2 mb-2">
            <AlertCircle className="w-4 h-4" />
            {t('privateLenderLoans.awaitingDisbursementBlock')}
          </h3>
          <div className="hidden md:grid md:grid-cols-[1fr_9rem_8rem_auto] gap-4 items-center text-sm border-b border-amber-200/60 pb-2 mb-2 text-gray-500 font-medium">
            <div>{t('privateLenderLoans.colProperty')}</div>
            <div className="text-left w-full">{t('privateLenderLoans.colAmount')}</div>
            <div className="text-left w-full">{t('privateLenderLoans.colStart')}</div>
            <div className="text-left min-w-[13.5rem]">{t('privateLenderLoans.colActions')}</div>
          </div>
          <ul className="space-y-2">
            {dealsAwaitingDisbursement.map((deal) => {
              const prop = getPropertyForDeal(deal);
              const addr = prop ? [prop.address, prop.city].filter(Boolean).join(', ') || prop.id : deal.propertyId;
              const canOpen = !!(prop && onOpenProperty);
              return (
                <li
                  key={deal.id}
                  className="flex flex-col gap-2 md:grid md:grid-cols-[1fr_9rem_8rem_auto] md:gap-4 md:items-center text-sm border border-amber-100 md:border-0 rounded-lg md:rounded-none p-3 md:p-0 bg-white/60 md:bg-transparent"
                >
                  <span className="min-w-0 text-gray-700">
                    {canOpen ? (
                      <button
                        type="button"
                        onClick={() => onOpenProperty(prop)}
                        className="text-indigo-600 hover:underline text-left break-words"
                      >
                        {addr}
                      </button>
                    ) : (
                      <span className="break-words">{addr}</span>
                    )}
                  </span>
                  <span className="text-gray-800 font-medium text-left tabular-nums w-full">
                    <span className="md:hidden text-gray-500 font-normal mr-1">{t('privateLenderLoans.colAmount')}:</span>
                    {formatAmount(deal.principalAmount)}
                  </span>
                  <span className="text-gray-700">
                    <span className="md:hidden text-gray-500 mr-1">{t('privateLenderLoans.colStart')}:</span>
                    {formatDate(deal.startDate)}
                  </span>
                  <span className="flex flex-wrap items-center justify-stretch md:justify-end gap-1 md:min-w-[12rem]">
                    <button
                      type="button"
                      disabled={markingDisbursed === deal.id || cancellingDealId === deal.id}
                      onClick={() => handleMarkDisbursed(deal.id)}
                      className="px-2 py-1 bg-amber-600 text-white rounded hover:bg-amber-700 disabled:opacity-50 text-xs flex-1 md:flex-none"
                    >
                      {markingDisbursed === deal.id ? '…' : t('privateLenderLoans.markDisbursed')}
                    </button>
                    <button
                      type="button"
                      disabled={markingDisbursed === deal.id || cancellingDealId === deal.id}
                      onClick={() => handleCancelDeal(deal)}
                      className="px-2 py-1 border border-red-600 text-red-600 rounded hover:bg-red-50 disabled:opacity-50 text-xs flex-1 md:flex-none"
                    >
                      {cancellingDealId === deal.id ? '…' : t('privateLenderLoans.cancelDeal')}
                    </button>
                  </span>
                </li>
              );
            })}
          </ul>
        </div>
      )}

      {/* Prêts à échéance sous peu */}
      {dealsMaturitySoon.length > 0 && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <h3 className="font-medium text-blue-900 flex items-center gap-2 mb-2">
            <Calendar className="w-4 h-4" />
            {t('privateLenderLoans.maturitySoon')}
          </h3>
          <ul className="space-y-1 text-sm text-gray-700">
            {dealsMaturitySoon.map((deal) => {
              const prop = getPropertyForDeal(deal);
              const addr = prop ? [prop.address, prop.city].filter(Boolean).join(', ') || prop.id : deal.propertyId;
              const days = daysUntil(deal.maturityDate);
              const canOpen = !!(prop && onOpenProperty);
              return (
                <li key={deal.id}>
                  {canOpen ? (
                    <button
                      type="button"
                      onClick={() => onOpenProperty(prop)}
                      className="text-indigo-600 hover:underline"
                    >
                      {addr}
                    </button>
                  ) : (
                    addr
                  )}{' '}
                  — {formatDate(deal.maturityDate)} ({days <= 30 ? '≤30' : days <= 60 ? '≤60' : '≤90'} {t('privateLenderLoans.days')})
                </li>
              );
            })}
          </ul>
        </div>
      )}

      {/* Tableau des prêts actifs */}
      <div className="bg-white rounded-b-lg shadow border border-gray-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">{t('privateLenderLoans.colProperty')}</th>
                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">{t('privateLenderLoans.colAmount')}</th>
                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">{t('privateLenderLoans.colRate')}</th>
                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">{t('privateLenderLoans.colStart')}</th>
                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">{t('privateLenderLoans.colMaturity')}</th>
                <th className="px-4 py-2 text-center text-xs font-medium text-gray-500 uppercase">{t('privateLenderLoans.colDisbursed')}</th>
                <th className="px-4 py-2 text-center text-xs font-medium text-gray-500 uppercase">{t('privateLenderLoans.colActions')}</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {disbursedDeals.length === 0 && dealsAwaitingDisbursement.length === 0 ? (
                <tr>
                  <td colSpan={7} className="px-4 py-6 text-center text-gray-500 text-sm">
                    {t('privateLenderLoans.noLoans')}
                  </td>
                </tr>
              ) : (
                [...disbursedDeals, ...dealsAwaitingDisbursement].map((deal) => {
                  const prop = getPropertyForDeal(deal);
                  const addr = prop ? [prop.address, prop.city].filter(Boolean).join(', ') || prop.id : deal.propertyId;
                  const clientLabel = prop?.clientId ? getClientLabel(prop.clientId) : null;
                  const canOpen = !!(prop && onOpenProperty);
                  return (
                    <tr key={deal.id} className="hover:bg-gray-50">
                      <td className="px-4 py-2 text-sm text-gray-800">
                        {clientLabel && <span className="block text-gray-500 text-xs">{clientLabel}</span>}
                        {canOpen ? (
                          <button
                            type="button"
                            onClick={() => onOpenProperty(prop)}
                            className="text-indigo-600 hover:underline"
                          >
                            {addr || deal.propertyId}
                          </button>
                        ) : (
                          addr || deal.propertyId
                        )}
                      </td>
                      <td className="px-4 py-2 text-sm text-left font-medium">{formatAmount(deal.principalAmount)}</td>
                      <td className="px-4 py-2 text-sm text-left">{deal.interestRate != null ? `${deal.interestRate} %` : '—'}</td>
                      <td className="px-4 py-2 text-sm text-gray-600">{formatDate(deal.startDate)}</td>
                      <td className="px-4 py-2 text-sm text-gray-600">{formatDate(deal.maturityDate)}</td>
                      <td className="px-4 py-2 text-sm text-center">
                        {deal.fundsDisbursedAt ? (
                          <span className="text-green-600">{t('privateLenderLoans.yes')}</span>
                        ) : (
                          <span className="text-amber-600">{t('privateLenderLoans.no')}</span>
                        )}
                      </td>
                      <td className="px-4 py-2 text-center">
                        <button
                          type="button"
                          onClick={() => openDetail(deal)}
                          className="text-indigo-600 hover:underline text-sm"
                        >
                          {t('privateLenderLoans.detail')}
                        </button>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Modale Détail du prêt + tranches */}
      {detailDeal && (
        <EscapeCloseLayer onClose={() => setDetailDeal(null)}>
        <div className="fixed inset-0 z-[220] rentalyzer-modal-overlay flex items-center justify-center p-4 bg-black/50">
          <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between p-4 border-b">
              <h3 className="text-lg font-semibold">{t('privateLenderLoans.detailTitle')}</h3>
              <button type="button" onClick={() => setDetailDeal(null)} className="p-1 rounded hover:bg-gray-100">
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="p-4 space-y-4">
              <div className="grid grid-cols-1 gap-2 text-sm">
                <span className="text-gray-500">{t('privateLenderLoans.colAmount')}</span>
                <span className="font-medium">{formatAmount(detailDeal.principalAmount)}</span>
                <span className="text-gray-500">{t('privateLenderLoans.colRate')}</span>
                <span>{detailDeal.interestRate != null ? `${detailDeal.interestRate} %` : '—'}</span>
                <span className="text-gray-500">{t('privateLenderLoans.colStart')}</span>
                <span>{formatDate(detailDeal.startDate)}</span>
                <span className="text-gray-500">{t('privateLenderLoans.colMaturity')}</span>
                <span>{formatDate(detailDeal.maturityDate)}</span>
              </div>
              {detailDeal.dealReference && (
                <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg">
                  <h4 className="font-medium text-amber-900 mb-1">{t('dealPayment.directDepositTitle')}</h4>
                  <p className="font-mono text-lg font-semibold text-amber-900 mb-1">{detailDeal.dealReference}</p>
                  <p className="text-sm text-amber-800">{t('dealPayment.refInteracHelpLender')}</p>
                </div>
              )}
              {!detailDeal.fundsDisbursedAt && (
                <div className="flex flex-wrap gap-2">
                  <button
                    type="button"
                    disabled={markingDisbursed === detailDeal.id || cancellingDealId === detailDeal.id}
                    onClick={() => handleMarkDisbursed(detailDeal.id)}
                    className="flex-1 min-w-[120px] px-3 py-2 bg-amber-600 text-white rounded-lg hover:bg-amber-700 disabled:opacity-50 text-sm"
                  >
                    {markingDisbursed === detailDeal.id ? '…' : t('privateLenderLoans.markDisbursed')}
                  </button>
                  <button
                    type="button"
                    disabled={markingDisbursed === detailDeal.id || cancellingDealId === detailDeal.id}
                    onClick={() => handleCancelDeal(detailDeal)}
                    className="flex-1 min-w-[120px] px-3 py-2 border border-red-600 text-red-600 rounded-lg hover:bg-red-50 disabled:opacity-50 text-sm"
                  >
                    {cancellingDealId === detailDeal.id ? '…' : t('privateLenderLoans.cancelDeal')}
                  </button>
                </div>
              )}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <h4 className="font-medium text-gray-800">{t('privateLenderLoans.tranches')}</h4>
                  <button
                    type="button"
                    onClick={() => openAddTranche(detailDeal.id)}
                    className="inline-flex items-center gap-1 px-2 py-1 text-sm text-indigo-600 hover:bg-indigo-50 rounded"
                  >
                    <Plus className="w-4 h-4" />
                    {t('privateLenderLoans.addTranche')}
                  </button>
                </div>
                <div className="border border-gray-200 rounded-lg overflow-x-auto">
                  <table className="min-w-full text-sm">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">{t('privateLenderLoans.trancheType')}</th>
                        <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">{t('privateLenderLoans.trancheEffectiveDate')}</th>
                        <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase">{t('privateLenderLoans.trancheAmount')}</th>
                        <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase w-24">{t('fundInvestors.table.actions')}</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200 bg-white">
                      {detailTranches.length === 0 ? (
                        <tr>
                          <td colSpan={4} className="px-3 py-4 text-center text-gray-500">{t('privateLenderLoans.noTranches')}</td>
                        </tr>
                      ) : (
                        detailTranches.map((tr) => {
                          const todayStr = new Date().toISOString().slice(0, 10);
                          const startStr = (tr.startDate || '').slice(0, 10);
                          const canDelete = startStr && startStr > todayStr;
                          return (
                            <tr key={tr.id}>
                              <td className="px-3 py-2 text-gray-800">{tr.type === 'initial' ? t('privateLenderLoans.trancheInitial') : t('privateLenderLoans.trancheAdditional')}</td>
                              <td className="px-3 py-2 text-gray-700">{formatDate(tr.startDate)}</td>
                              <td className="px-3 py-2 text-right font-medium tabular-nums">{formatAmount(tr.amount)}</td>
                              <td className="px-3 py-2 text-right">
                                {canDelete && (
                                  <button
                                    type="button"
                                    disabled={deletingTrancheId === tr.id}
                                    onClick={() => handleDeleteTranche(tr)}
                                    className="text-xs text-red-600 hover:underline disabled:opacity-50"
                                  >
                                    {deletingTrancheId === tr.id ? '…' : t('privateLenderLoans.deleteTranche')}
                                  </button>
                                )}
                              </td>
                            </tr>
                          );
                        })
                      )}
                    </tbody>
                  </table>
                </div>
                {trancheError && !addTrancheDealId && <p className="mt-1 text-sm text-red-600">{trancheError}</p>}
              </div>
              {/* Cédule des paiements d'intérêts */}
              {detailDeal.fundsDisbursedAt && (
                <div className="pt-4 border-t border-gray-200">
                  <h4 className="font-medium text-gray-800 mb-2">{t('privateLenderLoans.interestPaymentSection')}</h4>
                  {paymentSchedule.length === 0 ? (
                    <p className="text-sm text-gray-500">{t('privateLenderLoans.noSchedule')}</p>
                  ) : (
                    <div className="border border-gray-200 rounded-lg overflow-x-auto">
                      <table className="min-w-full text-sm">
                        <thead className="bg-gray-50">
                          <tr>
                            <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">{t('privateLenderLoans.schedulePeriod')}</th>
                            <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase">{t('privateLenderLoans.scheduleExpected')}</th>
                            <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">{t('privateLenderLoans.scheduleExpectedBy')}</th>
                            <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">{t('privateLenderLoans.status')}</th>
                            <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase">{t('privateLenderLoans.scheduleReceived')}</th>
                            <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase w-24">{t('fundInvestors.table.actions')}</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-200 bg-white">
                          {paymentSchedule.map((row) => (
                            <tr key={row.periodKey} className={row.periodKey === markPaymentPeriodKey ? 'bg-indigo-50/50' : ''}>
                              <td className="px-3 py-1.5 text-gray-700 font-medium whitespace-nowrap">
                                <button type="button" onClick={() => openPeriodDetail(row)} className="text-indigo-600 hover:underline whitespace-nowrap">
                                  {row.monthLabel}
                                </button>
                              </td>
                              <td className="px-3 py-1.5 text-right tabular-nums">{row.expectedAmount > 0 ? formatPaidAmountDisplay(row.expectedAmount) : '—'}</td>
                              <td className="px-3 py-1.5 text-gray-700">{formatDate(row.expectedByDate)}</td>
                              <td className="px-3 py-1.5">
                                <div className={row.status === 'paid' ? 'text-green-600' : row.status === 'partial' || row.status === 'overpaid' ? 'text-amber-600' : row.status === 'n/a' ? 'text-gray-400' : 'text-gray-600'}>
                                  {row.status === 'n/a' ? '—' : t(`privateLenderLoans.interestStatus_${row.status}`)}
                                </div>
                                {row.creditApplied > 0 && (
                                  <div className="text-xs text-gray-500">
                                    Crédit: {formatPaidAmountDisplay(row.creditApplied)}
                                  </div>
                                )}
                              </td>
                              <td className="px-3 py-1.5 text-right tabular-nums">{row.paidAmount != null ? formatPaidAmountDisplay(row.paidAmount) : '—'}</td>
                              <td className="px-3 py-1.5 text-right">
                                {(row.status === 'pending' || row.status === 'partial') && row.expectedAmount > 0 && (
                                  <button
                                    type="button"
                                    onClick={() => openMarkPaymentForm(row.periodKey, row.expectedAmount)}
                                    className="text-xs text-indigo-600 hover:underline"
                                  >
                                    {t('privateLenderLoans.markPaymentReceived')}
                                  </button>
                                )}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                  {showMarkPaymentForm && markPaymentPeriodKey && (() => {
                    const row = paymentSchedule.find((r) => r.periodKey === markPaymentPeriodKey);
                    return (
                      <form onSubmit={handleSavePayment} className="mt-3 space-y-3 p-3 bg-gray-50 rounded-lg border border-gray-200">
                        <p className="text-sm font-medium text-gray-700">{t('privateLenderLoans.interestPaymentSection')} — {row?.monthLabel}</p>
                        <div>
                          <label className="block text-xs font-medium text-gray-600 mb-0.5">{t('privateLenderLoans.paidAmount')} *</label>
                          <input
                            type="text"
                            inputMode="decimal"
                            placeholder="0.00 $"
                            value={markPaymentForm.paidAmount}
                            onChange={(e) => setMarkPaymentForm((f) => ({ ...f, paidAmount: e.target.value }))}
                            onBlur={(e) => {
                              const parsed = parsePaidAmountDisplay(e.target.value);
                              if (!Number.isNaN(parsed) && parsed >= 0) {
                                setMarkPaymentForm((f) => ({ ...f, paidAmount: formatPaidAmountDisplay(parsed) }));
                              }
                            }}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                          />
                        </div>
                        <div>
                          <label className="block text-xs font-medium text-gray-600 mb-0.5">{t('privateLenderLoans.paidAt')}</label>
                          <input
                            type="date"
                            value={markPaymentForm.paidAt}
                            onChange={(e) => setMarkPaymentForm((f) => ({ ...f, paidAt: e.target.value }))}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                          />
                        </div>
                        <div>
                          <label className="block text-xs font-medium text-gray-600 mb-0.5">{t('privateLenderLoans.discrepancyNote')}</label>
                          <textarea
                            value={markPaymentForm.discrepancyNote}
                            onChange={(e) => setMarkPaymentForm((f) => ({ ...f, discrepancyNote: e.target.value }))}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                            rows={2}
                          />
                        </div>
                        <div className="flex gap-2">
                          <button type="button" onClick={() => { setShowMarkPaymentForm(false); setMarkPaymentPeriodKey(null); }} className="px-3 py-2 border rounded-lg hover:bg-gray-100 text-sm">
                            {t('cancel')}
                          </button>
                          <button type="submit" disabled={markingPayment} className="px-3 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50 text-sm">
                            {markingPayment ? '…' : t('privateLenderLoans.savePayment')}
                          </button>
                        </div>
                      </form>
                    );
                  })()}
                </div>
              )}
            </div>
          </div>
        </div>
        </EscapeCloseLayer>
      )}

      {/* Modale Détail de la période (traçabilité + remboursement surpaiement) */}
      {periodDetailModal && (
        <EscapeCloseLayer
          onClose={() => {
            setPeriodDetailModal(null);
            setPeriodEvents([]);
          }}
        >
        <div className="fixed inset-0 z-[240] rentalyzer-modal-overlay rentalyzer-modal-overlay-stacked flex items-center justify-center p-4 bg-black/50">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl">
            <div className="flex items-center justify-between p-4 border-b">
              <h3 className="font-semibold text-gray-800">Détail — {periodDetailModal.monthLabel}</h3>
              <button type="button" onClick={() => { setPeriodDetailModal(null); setPeriodEvents([]); }} className="text-gray-500 hover:text-gray-700">
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="p-4 space-y-4">
              <div className="grid grid-cols-1 gap-3 text-sm">
                <div className="bg-gray-50 border rounded p-3">
                  <div className="text-xs text-gray-500 mb-1">Intérêt attendu</div>
                  <div className="font-medium">{periodDetailModal.expectedAmount > 0 ? formatPaidAmountDisplay(periodDetailModal.expectedAmount) : '—'}</div>
                </div>
                <div className="bg-gray-50 border rounded p-3">
                  <div className="text-xs text-gray-500 mb-1">Montant reçu (total)</div>
                  <div className="font-medium">{periodDetailModal.paidAmount != null ? formatPaidAmountDisplay(periodDetailModal.paidAmount) : '—'}</div>
                  {periodDetailModal.creditApplied > 0 && <div className="text-xs text-gray-500 mt-1">Crédit appliqué: {formatPaidAmountDisplay(periodDetailModal.creditApplied)}</div>}
                </div>
              </div>

              <div>
                <h4 className="font-medium text-gray-800 mb-2">Versements / événements</h4>
                {periodEventsLoading ? (
                  <p className="text-sm text-gray-500">Chargement…</p>
                ) : periodEvents.length === 0 ? (
                  <p className="text-sm text-gray-500">Aucun événement pour cette période.</p>
                ) : (
                  <div className="border border-gray-200 rounded-lg overflow-x-auto">
                    <table className="min-w-full text-sm">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Type</th>
                          <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
                          <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase">Montant</th>
                          <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Source</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-200 bg-white">
                        {periodEvents.map((ev) => {
                          const dt = ev.occurredAt?.toDate ? ev.occurredAt.toDate() : (ev.occurredAt ? new Date(ev.occurredAt) : null);
                          const dateStr = dt ? dt.toISOString().slice(0, 10) : '—';
                          return (
                            <tr key={ev.id}>
                              <td className="px-3 py-2 text-gray-700">{ev.type}</td>
                              <td className="px-3 py-2 text-gray-600">{dateStr}</td>
                              <td className="px-3 py-2 text-right tabular-nums">{ev.amount != null ? formatPaidAmountDisplay(Number(ev.amount) || 0) : '—'}</td>
                              <td className="px-3 py-2 text-gray-600">{ev.source || '—'}</td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>

              {periodDetailModal.status === 'overpaid' && periodDetailModal.expectedAmount > 0 && !periodEvents.some((ev) => ev.type === 'refund_issued') && (
                <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg">
                  <h4 className="font-medium text-amber-900 mb-2">Surpaiement</h4>
                  <p className="text-sm text-amber-800 mb-3">
                    Vous pouvez rembourser le surplus à l’emprunteur. Le montant est calculé automatiquement.
                  </p>
                  <form
                    onSubmit={async (e) => {
                      e.preventDefault();
                      if (!detailDeal?.id || !currentUser?.uid) return;
                      const paid = Number(periodDetailModal.paidAmount) || 0;
                      const expected = Number(periodDetailModal.expectedAmount) || 0;
                      const credit = Number(periodDetailModal.creditApplied) || 0;
                      const surplus = Math.max(0, (paid + credit) - expected);
                      if (surplus <= 0) return;
                      setSubmittingRefund(true);
                      try {
                        const occurredAt = refundForm.occurredAt ? new Date(refundForm.occurredAt + 'T12:00:00') : new Date();
                        await createRefundIssuedEvent(detailDeal.id, currentUser.uid, periodDetailModal.periodKey, periodDetailModal.periodStart, {
                          amount: surplus,
                          occurredAt,
                          note: refundForm.note || '',
                        });
                        const events = await getInterestPaymentEventsForPeriod(detailDeal.id, periodDetailModal.periodStart);
                        setPeriodEvents(events);
                      const payments = await getInterestPaymentsForDeal(detailDeal.id);
                      setDetailInterestPayments(payments);
                      } finally {
                        setSubmittingRefund(false);
                      }
                    }}
                    className="space-y-3"
                  >
                    <div className="grid grid-cols-1 gap-3">
                      <div>
                        <label className="block text-xs font-medium text-amber-900 mb-0.5">Date de remboursement</label>
                        <input type="date" value={refundForm.occurredAt} onChange={(e) => setRefundForm((f) => ({ ...f, occurredAt: e.target.value }))} className="w-full px-3 py-2 border border-amber-300 rounded-lg text-sm" />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-amber-900 mb-0.5">Montant (auto)</label>
                        <input type="text" value={formatPaidAmountDisplay(Math.max(0, ((Number(periodDetailModal.paidAmount) || 0) + (Number(periodDetailModal.creditApplied) || 0)) - (Number(periodDetailModal.expectedAmount) || 0)))} disabled className="w-full px-3 py-2 border border-amber-200 bg-amber-100/50 rounded-lg text-sm" />
                      </div>
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-amber-900 mb-0.5">Note (optionnel)</label>
                      <textarea value={refundForm.note} onChange={(e) => setRefundForm((f) => ({ ...f, note: e.target.value }))} rows={2} className="w-full px-3 py-2 border border-amber-300 rounded-lg text-sm" />
                    </div>
                    <button type="submit" disabled={submittingRefund} className="px-3 py-2 bg-amber-600 text-white rounded-lg hover:bg-amber-700 disabled:opacity-50 text-sm">
                      {submittingRefund ? '…' : 'Enregistrer le remboursement'}
                    </button>
                  </form>
                </div>
              )}
            </div>
          </div>
        </div>
        </EscapeCloseLayer>
      )}

      {/* Modale Ajouter une tranche */}
      {addTrancheDealId && (
        <EscapeCloseLayer onClose={() => setAddTrancheDealId(null)}>
        <div className="fixed inset-0 z-[230] rentalyzer-modal-overlay rentalyzer-modal-overlay-nested flex items-center justify-center p-4 bg-black/50">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full">
            <div className="flex items-center justify-between p-4 border-b">
              <h3 className="text-lg font-semibold">{t('privateLenderLoans.addTrancheTitle')}</h3>
              <button type="button" onClick={() => setAddTrancheDealId(null)} className="p-1 rounded hover:bg-gray-100">
                <X className="w-5 h-5" />
              </button>
            </div>
            <form onSubmit={handleAddTranche} className="p-4 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">{t('privateLenderLoans.trancheAmount')} *</label>
                <input
                  type="text"
                  value={trancheForm.amount}
                  onChange={(e) => setTrancheForm((f) => ({ ...f, amount: e.target.value }))}
                  onBlur={(e) => {
                    const raw = e.target.value.trim();
                    if (raw === '') { setTrancheForm((f) => ({ ...f, amount: '' })); return; }
                    const n = parsePaidAmountDisplay(raw);
                    if (!Number.isNaN(n) && n >= 0) setTrancheForm((f) => ({ ...f, amount: formatPaidAmountDisplay(n) }));
                  }}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
                  placeholder="0.00 $"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">{t('privateLenderLoans.trancheStartDate')} *</label>
                <input
                  type="date"
                  value={trancheForm.startDate}
                  onChange={(e) => setTrancheForm((f) => ({ ...f, startDate: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">{t('privateLenderLoans.trancheRate')}</label>
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  value={trancheForm.interestRate}
                  onChange={(e) => setTrancheForm((f) => ({ ...f, interestRate: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
                  placeholder="%"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">{t('privateLenderLoans.trancheNote')}</label>
                <textarea
                  value={trancheForm.note}
                  onChange={(e) => setTrancheForm((f) => ({ ...f, note: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
                  rows={2}
                />
              </div>
              {trancheError && <p className="text-sm text-red-600">{trancheError}</p>}
              <div className="flex justify-end gap-2">
                <button type="button" onClick={() => setAddTrancheDealId(null)} className="px-4 py-2 border rounded-lg hover:bg-gray-50">
                  {t('cancel')}
                </button>
                <button type="submit" disabled={addingTranche} className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50">
                  {addingTranche ? '…' : t('privateLenderLoans.addTrancheSubmit')}
                </button>
              </div>
            </form>
          </div>
        </div>
        </EscapeCloseLayer>
      )}
    </div>
  );
};

export default PrivateLenderLoansPage;
